import React, { Component } from 'react';
import classNames from 'classnames';
import styled from '@emotion/styled';
import { CSSObject } from '@emotion/serialize';
import { SPACING } from 'Style/spacing';
import { TEXT_COLORS } from 'Style/colors';

import withT from 'ComponentLibrary/hocs/withT';

import connector from 'Utils/connector';
import connectSendUsageEventWithSession from 'Webapp/shared/app/connectors/connectSendUsageEventWithSession';

import { isWebAdsEnabled } from './ads-config';
import { maybeFetchFlintTargeting } from './flint';
import {
  getFlintEids,
  maybeInitAdsIdentity,
  selectAdsIdentityEmail,
  selectAdsIdentityUid,
} from './identity';
import {
  AdSize,
  GptAdSize,
  FLUID,
  SizeMappingEntry,
  AdSlotHandle,
  GptSlotRenderEndedEvent,
  defineAndDisplayAdSlot,
  nextAdDivId,
} from './gpt';
import { NativeAdFields, registerNativeAdCallback } from './native-ad-render';

// Load lazy slots slightly before they enter the viewport.
const LAZY_ROOT_MARGIN = '200px 0px';

type ReservedHeight = { minWidth: number; height: number };

const tallestHeight = (sizes: AdSize[]): number =>
  sizes.reduce((max, [, height]) => Math.max(max, height), 0);

/**
 * Fixed (reserved) ad-area heights per breakpoint: always reserve the
 * tallest configured creative for the active viewport so nothing shifts
 * when the ad renders — and keep the space when GPT reports an empty slot.
 */
export const computeReservedHeights = (
  sizes: AdSize[],
  sizeMapping?: SizeMappingEntry[],
): ReservedHeight[] => {
  if (!sizeMapping || sizeMapping.length === 0) {
    return [{ minWidth: 0, height: tallestHeight(sizes) }];
  }
  const heights = [...sizeMapping]
    .sort((a, b) => a.viewport[0] - b.viewport[0])
    .map((entry) => ({
      minWidth: entry.viewport[0],
      height: tallestHeight(entry.sizes),
    }));
  if (heights[0].minWidth !== 0) {
    heights.unshift({ minWidth: 0, height: tallestHeight(sizes) });
  }
  return heights;
};

const StyledAdLabel = styled('div', {
  shouldForwardProp: (prop) => prop !== 'filled',
})<{ filled: boolean }>((props) => ({
  color: TEXT_COLORS.tertiary,
  fontSize: '10px',
  lineHeight: '16px',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  textAlign: 'center',
  marginBottom: SPACING.SMALL,
  // The label always occupies its space (zero layout shift) but is only
  // visible once a creative has rendered — an unfilled slot reads as plain
  // whitespace instead of a broken ad.
  visibility: props.filled ? 'visible' : 'hidden',
}));

const StyledAdArea = styled('div', {
  shouldForwardProp: (prop) => prop !== 'reservedHeights',
})<{ reservedHeights: ReservedHeight[] }>((props) => {
  const [base, ...rest] = props.reservedHeights;
  const style: CSSObject = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    height: `${base.height}px`,
  };
  rest.forEach(({ minWidth, height }) => {
    style[`@media (min-width: ${minWidth}px)`] = { height: `${height}px` };
  });
  return style;
});

export type AdSlotPassedProps = {
  // Short slot name, used for div ids / class names (e.g. "leaderboard").
  name: string;
  // Full GAM ad unit path, e.g. /21709104563/web/leaderboard.
  adUnitPath: string;
  // Base creative sizes passed to defineSlot.
  sizes: AdSize[];
  // Optional responsive size mapping (defineSizeMapping).
  sizeMapping?: SizeMappingEntry[];
  // Lazy (IntersectionObserver, default) vs eager ad request.
  lazy?: boolean;
  className?: string;
  // Slot-level key-values (page_type, topic_slug, ...).
  targeting?: Record<string, string | Array<string>>;
  // Placement feature flag; the slot renders nothing unless BOTH the
  // WEB_ADS master flag and this flag are active.
  placementFlag: string;
  // Show the "Advertisement" label above the ad (when one renders).
  // Required for placements that could be mistaken for content (the in-feed
  // card in the story grid — FTC native-advertising guidance / IAB native
  // standards); off by default for visually-distinct banners (leaderboard,
  // rail), matching how the apps treat banners.
  showLabel?: boolean;
  // Self-rendered NATIVE mode: request a ['fluid'] slot (GAM native format)
  // instead of a fixed display banner, and render the native creative's fields
  // via `renderNative` into our own card (see native-ad-render.ts). No fixed
  // height is reserved; the slot shows nothing until fields arrive and hides on
  // no-fill. `sizes` is ignored for the GPT request in native mode (['fluid']
  // is used) but is still passed to flint for bid-size context.
  native?: boolean;
  // Render-prop for native mode: given the received native fields, returns the
  // card markup. Not called until fields arrive; the slot renders nothing
  // (hidden tile) otherwise.
  renderNative?: (fields: NativeAdFields) => React.ReactNode;
};

// Selector-only equivalent of connectFeatureFlags (keeps this component's
// import graph light — no action creators needed here). uid/email feed the
// consent-gated ads identity bootstrap (WEB_ADS_IDENTITY, ./identity.ts);
// email is only present when profile.userInfo happens to be loaded.
const selectors = {
  featureFlags: ({ featureFlags }: Flipboard.State) => featureFlags,
  uid: selectAdsIdentityUid,
  currentUserEmail: selectAdsIdentityEmail,
};

type AdSlotProps = Flipboard.ConnectorProps<typeof selectors> &
  AdSlotPassedProps & {
    t: Flipboard.TFunction;
    sendUsageEventWithSession?: (event: Record<string, unknown>) => void;
  };

type AdSlotState = {
  divId: string | null;
  filled: boolean;
  // Native mode only: the received native creative fields (null until the GAM
  // native style hands them to native-ad-render.ts, or on no-fill).
  nativeFields: NativeAdFields | null;
};

/**
 * Reusable GPT display-ad slot.
 *
 * - Renders a fixed-height, breakpoint-aware container (zero layout shift)
 *   with a subtle "Advertisement" label; the reserved space is intentionally
 *   kept when GPT returns an empty slot (no collapse at launch).
 * - Defines/displays the slot inside googletag.cmd callbacks; enableServices
 *   is coordinated once globally (see gpt.ts); destroySlots on unmount.
 * - Div ids are unique per mounted instance and assigned client-side only,
 *   so SSR markup stays deterministic.
 */
export class AdSlot extends Component<AdSlotProps, AdSlotState> {
  state: AdSlotState = {
    divId: null,
    filled: false,
    nativeFields: null,
  };

  componentDidMount() {
    if (this.isEnabled()) {
      this.setState({ divId: nextAdDivId(this.props.name) });
    }
  }

  componentDidUpdate(prevProps: AdSlotProps, prevState: AdSlotState) {
    // Flags flipped off after mount: render() already returns null, but the
    // observer and GPT slot handle would otherwise outlive the removed div.
    if (!this.isEnabled() && this.state.divId) {
      this.disconnectObserver();
      this.unregisterNative();
      if (this.handle) {
        this.handle.destroy();
        this.handle = null;
      }
      this.setState({ divId: null, filled: false, nativeFields: null });
      return;
    }
    // Flags flipped on after mount (e.g. dev tooling)
    if (this.isEnabled() && !this.state.divId) {
      this.setState({ divId: nextAdDivId(this.props.name) });
      return;
    }
    // The ad div (with its new id) is now committed to the DOM; safe to
    // request (also covers re-requests after resetAd).
    if (prevState.divId !== this.state.divId && this.state.divId) {
      this.setupAdRequest();
      return;
    }
    // Slot-level targeting changed while the slot stayed mounted (SPA
    // navigation, e.g. the persistent leaderboard when moving from one topic
    // to another): destroy the old slot and request a fresh one so the ad
    // matches the new page.
    if (
      this.state.divId &&
      JSON.stringify(prevProps.targeting) !==
        JSON.stringify(this.props.targeting)
    ) {
      this.resetAd();
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.disconnectObserver();
    this.unregisterNative();
    if (this.handle) {
      this.handle.destroy();
      this.handle = null;
    }
  }

  adAreaRef = React.createRef<HTMLDivElement>();
  handle: AdSlotHandle | null = null;
  observer: IntersectionObserver | null = null;
  unmounted = false;
  // Unregisters this slot's native-fields callback (native mode only). No-op
  // until a native request registers one.
  unregisterNative: () => void = () => undefined;
  // Div id of the ad div whose flint+GPT request is in flight. Keyed by div
  // id (not a boolean) so a resetAd() during the flint query can't block the
  // new div's request — only a duplicate request for the SAME div is a no-op.
  flintRequestDivId: string | null = null;

  isEnabled = () => {
    const { featureFlags, placementFlag } = this.props;
    return (
      isWebAdsEnabled(featureFlags) &&
      !!(featureFlags as Record<string, boolean>)?.[placementFlag]
    );
  };

  resetAd = () => {
    this.disconnectObserver();
    this.unregisterNative();
    if (this.handle) {
      this.handle.destroy();
      this.handle = null;
    }
    // New div id → componentDidUpdate kicks off a fresh request once the
    // new ad div is committed.
    this.setState({
      divId: nextAdDivId(this.props.name),
      filled: false,
      nativeFields: null,
    });
  };

  setupAdRequest = () => {
    const { lazy = true, featureFlags, uid, currentUserEmail } = this.props;
    // Warm identity (PPID / RampID envelope) as soon as the slot exists —
    // fire-and-forget, so consent reads and the envelope fetch overlap the
    // lazy-load wait instead of ever delaying a GPT request. requestAd()
    // does NOT await this: GPT applies the PPID to every request made after
    // maybeInitAdsIdentity's googletag.cmd runs (worst case, an eager
    // first request goes out unidentified — never late).
    void maybeInitAdsIdentity({
      featureFlags: featureFlags as Partial<Record<string, boolean>>,
      uid,
      email: currentUserEmail,
    });
    const adAreaNode = this.adAreaRef.current;
    // Only ever runs client-side (from componentDidUpdate); fall back to an
    // eager request in browsers without IntersectionObserver.
    if (lazy && typeof IntersectionObserver === 'function' && adAreaNode) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.disconnectObserver();
            this.requestAd();
          }
        },
        { rootMargin: LAZY_ROOT_MARGIN },
      );
      this.observer = observer;
      observer.observe(adAreaNode);
    } else {
      this.requestAd();
    }
  };

  disconnectObserver = () => {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  };

  requestAd = async () => {
    const { divId } = this.state;
    if (this.handle || !divId || this.flintRequestDivId === divId) {
      return;
    }
    const { adUnitPath, sizes, sizeMapping, targeting, featureFlags, native } =
      this.props;
    this.flintRequestDivId = divId;
    // Ads identity (WEB_ADS_IDENTITY): consent-gated, once-per-page
    // bootstrap that sets the GPT PPID (enqueued onto googletag.cmd BEFORE
    // this slot's defineSlot, since we await it here) and kicks off the
    // LiveRamp envelope fetch (GPT secure signals + flint eids). No-op when
    // the flag is off; never rejects; bounded by the CMP consent timeouts.
    //
    // flint header bidding (WEB_ADS_FLINT): query Flipboard's in-house
    // bidder before the GPT request and merge the winning bid's key-values
    // (FLCPM/flint_bidder/...) into the slot targeting so the GAM
    // price-priority line items can arbitrate it against AdX. Resolves
    // immediately with {} when the flag is off, and fails open (also {})
    // on timeout/error — the GPT request is never blocked beyond flint's
    // time budget. Identity init was kicked off (unawaited) in
    // setupAdRequest — flint's eids provider caps its own wait on the
    // envelope, so nothing here extends flint's budget and a flint-off
    // request proceeds immediately.
    const flags = featureFlags as Partial<Record<string, boolean>>;
    const flintTargeting = await maybeFetchFlintTargeting(flags, {
      sizes,
      sizeMapping,
      pageType:
        typeof targeting?.page_type === 'string'
          ? targeting.page_type
          : undefined,
      topicSlug:
        typeof targeting?.topic_slug === 'string'
          ? targeting.topic_slug
          : undefined,
      eidsProvider: getFlintEids,
    });
    if (this.flintRequestDivId === divId) {
      this.flintRequestDivId = null;
    }
    // The slot may have been unmounted, disabled, or reset (new div id)
    // while the flint query was in flight.
    if (this.unmounted || this.handle || this.state.divId !== divId) {
      return;
    }
    // Native mode: register to receive the native creative's fields (the GAM
    // native style pushes them up via native-ad-render.ts) BEFORE the GPT
    // request, so a friendly-iframe creative that fires synchronously on
    // display() finds a callback waiting. GPT requests a ['fluid'] slot.
    let gptSizes: GptAdSize[] = sizes;
    if (native) {
      gptSizes = [FLUID];
      this.unregisterNative = registerNativeAdCallback(divId, (fields) => {
        if (this.unmounted || this.state.divId !== divId) {
          return;
        }
        // Fields arriving marks the slot filled (and shows the label/card).
        this.setState({ nativeFields: fields, filled: true });
      });
    }
    this.handle = defineAndDisplayAdSlot({
      adUnitPath,
      sizes: gptSizes,
      sizeMapping,
      targeting: { ...targeting, ...flintTargeting },
      divId,
      onSlotRenderEnded: this.handleSlotRenderEnded,
    });
  };

  handleSlotRenderEnded = (event: GptSlotRenderEndedEvent) => {
    // Launch decision: when the slot comes back empty we KEEP the reserved
    // space (no collapse) so the layout never shifts — but the
    // "Advertisement" label only shows once a creative actually renders,
    // so unfilled slots read as whitespace rather than a broken ad. The
    // data attribute is for QA/styling hooks.
    const node = this.adAreaRef.current;
    if (node) {
      node.setAttribute('data-ad-status', event.isEmpty ? 'empty' : 'filled');
    }
    // Client-side ad telemetry — parity with iOS/Android, whose ad outcomes are
    // visible via GAM Data Transfer. Web display fill/empty is otherwise only
    // measurable server-side; emit a usage event so per-slot no-fill rate (and,
    // by slot/ad_unit, where users see whitespace) is measurable. For native,
    // isEmpty is the true no-fill signal; a non-empty native render is only
    // confirmed once fields arrive (native-ad-render), so treat native
    // display_filled as "slot returned a creative", not "card shown".
    this.props.sendUsageEventWithSession?.({
      event_category: 'ad',
      event_action: event.isEmpty ? 'display_empty' : 'display_filled',
      // Keys must map to real usage_event columns (method / target_id / item_type)
      // to survive the pipeline — custom keys (surface/ad_unit/slot) are dropped.
      event_data: {
        method: 'display_web',
        target_id: this.props.adUnitPath,
        item_type: this.props.native ? 'native' : 'banner',
      },
    });
    // Native mode: fill is signalled by the native fields arriving (the
    // callback in requestAd), NOT slotRenderEnded — a non-empty native render
    // still has no card until its fields are handed up. An EMPTY native slot
    // is a no-fill: clear any fields so the tile hides.
    if (this.props.native) {
      if (event.isEmpty) {
        this.setState({ filled: false, nativeFields: null });
      }
      return;
    }
    this.setState({ filled: !event.isEmpty });
  };

  render() {
    if (!this.isEnabled()) {
      return null;
    }
    const {
      className,
      name,
      showLabel,
      sizes,
      sizeMapping,
      native,
      renderNative,
      t,
    } = this.props;
    const { divId, filled, nativeFields } = this.state;

    // Native mode: no reserved-height box. The GPT ['fluid'] div is a hidden
    // host for the GAM native style iframe (which posts fields up and renders
    // nothing visible itself); the visible card is our own render of the
    // received fields. Until fields arrive — and on no-fill — nothing is
    // rendered, so the story-grid tile collapses (matching the display slot's
    // empty-hide via the parent card's onStatusChange).
    if (native) {
      return (
        <div className={classNames('ad-slot', `ad-slot--${name}`, className)}>
          {/* Off-screen host for the fluid native creative iframe. */}
          <div
            ref={this.adAreaRef}
            className="ad-slot__native-host"
            style={{ display: 'none' }}
          >
            {divId && <div id={divId} />}
          </div>
          {nativeFields && renderNative && (
            <div className="ad-slot__native-card">
              {showLabel && (
                <StyledAdLabel className="ad-slot__label" filled={filled}>
                  {t('advertisement')}
                </StyledAdLabel>
              )}
              {renderNative(nativeFields)}
            </div>
          )}
        </div>
      );
    }

    const reservedHeights = computeReservedHeights(sizes, sizeMapping);
    return (
      <div className={classNames('ad-slot', `ad-slot--${name}`, className)}>
        {showLabel && (
          <StyledAdLabel className="ad-slot__label" filled={filled}>
            {t('advertisement')}
          </StyledAdLabel>
        )}
        <StyledAdArea
          className="ad-slot__container"
          reservedHeights={reservedHeights}
          ref={this.adAreaRef}
        >
          {divId && <div id={divId} />}
        </StyledAdArea>
      </div>
    );
  }
}

export default connector<AdSlotProps>(
  { selectors },
  connectSendUsageEventWithSession,
)(withT(AdSlot));
